import { useCallback, useRef, useState } from 'react';
import { getWaterIntelRead } from '../services/waterIntel/waterIntelClient';

type WaterIntelRead = Awaited<ReturnType<typeof getWaterIntelRead>>;

type Anchor = { lat: number; lng: number };

export function useWaterIntelRead() {
  const [read, setRead] = useState<WaterIntelRead | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestSeqRef = useRef(0);

  const load = useCallback(async (anchor: Anchor) => {
    const lat = Number(anchor?.lat);
    const lng = Number(anchor?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setError('Move the map to an area first.');
      return null;
    }

    const seq = ++requestSeqRef.current;
    setLoading(true);
    setError(null);

    try {
      const next = await getWaterIntelRead({
        lat,
        lng,
        requestedCapabilities: [
          'basic_current',
          'basic_depth',
          'structure_analysis',
        ],
      });
      if (seq !== requestSeqRef.current) return next;
      setRead(next);
      return next;
    } catch (err: any) {
      if (seq !== requestSeqRef.current) return null;
      const message = String(err?.message || 'Water Intel is unavailable right now.');
      setError(message);
      return null;
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, []);

  const clear = useCallback(() => {
    requestSeqRef.current += 1;
    setRead(null);
    setError(null);
    setLoading(false);
  }, []);

  return { read, loading, error, load, clear };
}
